import { useEffect } from "react"
import { Building2, Users, TrendingUp, TrendingDown, Wrench, Wallet, ArrowUpRight, Activity, HelpCircle } from "lucide-react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts"

import { Link } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { APP_NAME } from "@/lib/appConfig"
import { useDashboardStats } from "@/features/finances/hooks/useFinances"
import { useAuthStore } from "@/stores/authStore"
import { useCurrency } from "@/hooks/useCurrency"
import { PageHeader } from "@/components/shared/PageHeader"
import { StatCardSkeleton } from "@/components/shared/LoadingSkeleton"
import { PullToRefresh } from "@/components/shared/PullToRefresh"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { GettingStartedCard } from "@/components/shared/OnboardingWizard"

function getGreeting() {
  const hour = new Date().getHours()
  if (hour < 12) return "Good morning"
  if (hour < 17) return "Good afternoon"
  return "Good evening"
}

interface StatCardProps {
  label: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  action?: React.ReactNode
  color: {
    bg: string
    text: string
    border: string
    shadow: string
    iconBg: string
  }
}

function StatCard({ label, value, subtitle, icon, action, color }: StatCardProps) {
  return (
    <Card className={`relative rounded-[2rem] border transition-all duration-300 ${color.bg} ${color.border} ${color.shadow} hover:shadow-xl hover:-translate-y-1 group h-full cursor-pointer`}>
      {/* Decorative gradient blur background wrapped in overflow-hidden */}
      <div className="absolute inset-0 rounded-[2rem] overflow-hidden pointer-events-none z-0">
        <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 blur-2xl pointer-events-none group-hover:bg-white/20 transition-all duration-500" />
      </div>

      <CardContent className="p-6 relative z-10 flex flex-col h-full">
        <div className="flex justify-between items-start mb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <p className={`text-sm font-medium tracking-wider uppercase ${color.text} opacity-80`}>{label}</p>
              {action}
            </div>
            <h3 className="text-4xl font-extrabold tracking-tight text-foreground">{value}</h3>
          </div>
          <div className={`w-14 h-14 rounded-2xl ${color.iconBg} flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-sm backdrop-blur-md relative z-10`}>
            {icon}
          </div>
        </div>

        {subtitle && (
          <div className="flex items-center gap-2 mt-auto relative z-10">
            <div className="h-1.5 w-1.5 rounded-full bg-current opacity-60 flex-shrink-0" />
            <p className="text-sm font-medium text-muted-foreground">{subtitle}</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default function DashboardPage() {
  const { user } = useAuthStore()
  const { format } = useCurrency()
  const { data: stats, isLoading } = useDashboardStats()
  const queryClient = useQueryClient()

  useEffect(() => {
    const prev = document.title
    document.title = `Dashboard | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  const collectionPct =
    stats && stats.rent_expected > 0
      ? Math.round((stats.rent_collected / stats.rent_expected) * 100)
      : 0

  const chartData = stats?.historical_cashflow ?? []
  const hasChartData = chartData.some((d) => d.revenue > 0 || d.expenses > 0)

  return (
    <div className="flex flex-col h-full bg-slate-50/50 dark:bg-background/95">
      <PageHeader title="Overview" />

      <PullToRefresh onRefresh={() => queryClient.invalidateQueries()} className="flex-1 pb-nav">
        <div className="px-6 pt-6 pb-2 max-w-[1600px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-3">
                {getGreeting()}, {user?.full_name?.split(" ")[0] ?? "there"}
                <span className="text-2xl">👋</span>
              </h1>
              <p className="text-muted-foreground mt-1 text-lg">Here's what's happening with your portfolio today.</p>
            </div>
          </div>

          <GettingStartedCard
            propertyCount={stats?.total_properties ?? -1}
            tenantCount={stats?.total_tenants ?? -1}
          />

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <StatCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              <Link to="/properties" className="block h-full outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-[2rem]">
                <StatCard
                  label="Properties"
                  value={stats?.total_properties ?? 0}
                  subtitle={`${stats?.occupied_properties ?? 0} occupied • ${stats?.available_properties ?? 0} vacant`}
                  icon={<Building2 className="h-7 w-7 text-blue-600 dark:text-blue-400" />}
                  color={{
                    bg: "bg-white dark:bg-card/40",
                    text: "text-slate-500 dark:text-slate-400",
                    border: "border-slate-200/60 dark:border-border/50",
                    shadow: "shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none",
                    iconBg: "bg-blue-50 dark:bg-blue-500/10"
                  }}
                />
              </Link>
              <Link to="/tenants" className="block h-full outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-[2rem]">
                <StatCard
                  label="Tenants"
                  value={stats?.total_tenants ?? 0}
                  subtitle={`${stats?.active_leases ?? 0} active leases`}
                  icon={<Users className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />}
                  color={{
                    bg: "bg-white dark:bg-card/40",
                    text: "text-slate-500 dark:text-slate-400",
                    border: "border-slate-200/60 dark:border-border/50",
                    shadow: "shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none",
                    iconBg: "bg-indigo-50 dark:bg-indigo-500/10"
                  }}
                />
              </Link>
              <Link to="/finances" className="block h-full outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-[2rem]">
                <StatCard
                  label="Net Income"
                  value={stats ? format((stats.monthly_income ?? 0) - (stats.monthly_expenses ?? 0)) : "—"}
                  subtitle="This month"
                  icon={<Wallet className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />}
                  color={{
                    bg: "bg-gradient-to-br from-emerald-50 to-teal-50/50 dark:from-emerald-950/20 dark:to-teal-900/10",
                    text: "text-emerald-600 dark:text-emerald-400",
                    border: "border-emerald-100/50 dark:border-emerald-900/30",
                    shadow: "shadow-[0_8px_30px_rgb(16,185,129,0.1)] dark:shadow-none",
                    iconBg: "bg-white/60 dark:bg-emerald-500/20"
                  }}
                />
              </Link>
              <Link to="/maintenance" className="block h-full outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-[2rem]">
                <StatCard
                  label="Maintenance"
                  value={stats?.open_maintenance_issues ?? 0}
                  subtitle="Open issues requiring attention"
                  icon={<Wrench className="h-7 w-7 text-amber-600 dark:text-amber-400" />}
                  action={
                    <div className="relative group/tooltip flex items-center" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <button className="text-slate-400 hover:text-amber-500 transition-colors bg-transparent border-none p-0 cursor-help outline-none">
                        <HelpCircle className="h-4 w-4" />
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2.5 bg-slate-800 text-slate-100 text-xs rounded-lg shadow-xl opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-50 text-center flex flex-col items-center">
                        This reflects property maintenance schedules, ongoing repairs, and issue tracking.
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                      </div>
                    </div>
                  }
                  color={{
                    bg: "bg-white dark:bg-card/40",
                    text: "text-slate-500 dark:text-slate-400",
                    border: "border-slate-200/60 dark:border-border/50",
                    shadow: "shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none",
                    iconBg: "bg-amber-50 dark:bg-amber-500/10"
                  }}
                />
              </Link>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Main Chart Area */}
            <div className="lg:col-span-2">
              <Card className="rounded-[2rem] border-border/50 bg-white/60 dark:bg-card/40 backdrop-blur-xl shadow-soft h-full flex flex-col">
                <CardHeader className="p-6 md:p-8 pb-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl font-bold flex items-center gap-2">
                        <Activity className="h-5 w-5 text-primary" />
                        Cash Flow Overview
                      </CardTitle>
                      <CardDescription className="text-base mt-1">Revenue vs Expenses for the last 6 months</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 md:p-8 pt-6 flex-1 min-h-[350px] flex flex-col justify-center">
                  {!isLoading && !hasChartData ? (
                    <div className="flex flex-col items-center justify-center h-full w-full py-12 text-center">
                      <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800/50 flex items-center justify-center mb-4">
                        <Activity className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                      </div>
                      <p className="text-lg font-semibold text-slate-700 dark:text-slate-300">No financial data yet</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs mx-auto">
                        Your cash flow history will appear here once you collect rent and record expenses.
                      </p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={350}>
                      <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.4} />
                        <XAxis
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 13 }}
                          dy={10}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 13 }}
                          tickFormatter={(value) => `€${value / 1000}k`}
                          dx={-10}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            borderRadius: '16px',
                            border: '1px solid hsl(var(--border))',
                            boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
                            backgroundColor: 'hsl(var(--card))',
                            padding: '12px 16px',
                          }}
                          itemStyle={{ fontSize: '14px', fontWeight: 500 }}
                          formatter={(value: number) => [format(value), undefined]}
                        />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          name="Revenue"
                          stroke="#10b981"
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#colorRevenue)"
                          activeDot={{ r: 6, strokeWidth: 0, fill: '#10b981' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="expenses"
                          name="Expenses"
                          stroke="#ef4444"
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#colorExpenses)"
                          activeDot={{ r: 6, strokeWidth: 0, fill: '#ef4444' }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar Stats */}
            <div className="space-y-6">
              {/* Rent Collection */}
              <Card className="rounded-[2rem] border-border/50 bg-white/60 dark:bg-card/40 backdrop-blur-xl shadow-soft relative overflow-hidden group">
                <CardContent className="p-8 relative z-10">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Collection Rate</p>
                      <p className="text-xl font-bold tracking-tight mt-1 text-foreground">This Month</p>
                    </div>
                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center border-4 border-primary/20 backdrop-blur-sm">
                      <span className="text-xl font-bold text-primary">{collectionPct}%</span>
                    </div>
                  </div>

                  {/* Premium Glowing Progress Bar */}
                  <div className="relative h-4 w-full bg-slate-100 dark:bg-slate-800/50 rounded-full overflow-hidden shadow-inner mb-6">
                    <div
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary via-indigo-400 to-primary bg-[length:200%_auto] animate-gradient rounded-full shadow-[0_0_15px_rgba(var(--primary),0.5)] transition-all duration-1000 ease-out"
                      style={{ width: `${collectionPct}%` }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">Collected</p>
                      <p className="font-bold text-emerald-600 dark:text-emerald-400 text-lg">{format(stats?.rent_collected ?? 0)}</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">Expected</p>
                      <p className="font-bold text-slate-600 dark:text-slate-300 text-lg">{format(stats?.rent_expected ?? 0)}</p>
                    </div>
                  </div>
                </CardContent>
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none group-hover:bg-primary/10 transition-colors duration-500" />
              </Card>

              {/* Financial Summary */}
              <Card className="rounded-[2rem] border-border/50 bg-white/60 dark:bg-card/40 backdrop-blur-xl shadow-soft">
                <CardContent className="p-8">
                  <h3 className="text-lg font-bold tracking-tight text-foreground flex items-center gap-2 mb-6">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Financial Summary
                  </h3>

                  <div className="space-y-5">
                    <div className="flex justify-between items-center group">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
                          <ArrowUpRight className="h-5 w-5" />
                        </div>
                        <span className="font-medium text-slate-600 dark:text-slate-300 text-base">Income</span>
                      </div>
                      <span className="font-bold text-emerald-600 dark:text-emerald-400 text-lg">
                        +{format(stats?.monthly_income ?? 0)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center group">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center text-rose-600 dark:text-rose-400 group-hover:scale-110 transition-transform">
                          <TrendingDown className="h-5 w-5" />
                        </div>
                        <span className="font-medium text-slate-600 dark:text-slate-300 text-base">Expenses</span>
                      </div>
                      <span className="font-bold text-rose-600 dark:text-rose-400 text-lg">
                        -{format(stats?.monthly_expenses ?? 0)}
                      </span>
                    </div>

                    <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent w-full my-4" />

                    <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                      <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Net Cash Flow</span>
                      <span className="font-black text-foreground text-2xl tracking-tight">
                        {format((stats?.monthly_income ?? 0) - (stats?.monthly_expenses ?? 0))}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

        </div>
      </PullToRefresh>
    </div>
  )
}
